import { Economy, PurchaseService, Store, dealProducts } from "@highmarch/app";
import { fmtCompact, pillHtml } from "../ui/kit";
import { toast } from "../ui/toast";
import { ReminderIds, registerReminderSource } from "../infra/platform/reminders";
import { gameStore, sandboxStore } from "../infra/store/openStore";
import { showDealsDebug } from "../ui/dealsDebugSheet";
import { saveStore } from "../infra/save/openSaveStore";
import { economyLink, setEconomyLink } from "../ui/hub/economyLink";
import { currencyIconUrl } from "../ui/hub/econArt";
import { openShopHub } from "../ui/hub/shopSheet";
import { openDeals } from "../ui/deals/dealsSheet";
import { dealsLink, extraTabsDot } from "../ui/deals/dealsLink";
import { econCue } from "../ui/hub/purchaseFx";
import { TRIBUTE_REMINDER, mountTributeChip } from "../ui/hub/tributeChip";
import { QUEUE_TOPIC, SHOP_TOPIC } from "../ui/hub/econTopics";
import { MORE_ORDER, type Feature, type FeatureContext, type HubApi } from "./registry";
import "../ui/styles/shop.css";

/**
 * The offline economy's hub side (FINISH_PLAN WP5; ADR 0013): the gems pill (its "+" opens the Shop's gem packs), the
 * Shop tab (M35: one page per currency, ui/hub/shopSheet.ts — every currency pill opens its page), the Tribute chip on the fortress with its "full" reminder, the Guide's shop and queue topics,
 * and the real-money gem packs — `PurchaseService` over the platform's `Store` (sandbox on the web, StoreKit 2 in the
 * iOS shell), built here at boot: it delivers what a crash left unfinished, then listens for late transactions (Ask
 * to Buy, refunds); Settings → Restore purchases asks the store again. The rules are @highmarch/app's (Economy,
 * PurchaseService); this is glue. The Market (⚙ → Market, Shop → Marks) is features/market.ts.
 * Deals (docs/SHOP.md): PurchaseService also grants Deals products (`catalog.deals`), asks the store for its storefront
 * at boot; the Deals screen itself (and VIP's daily XP claim) is features/deals.ts.
 */

/** Top-bar pill order: gems, then Marks (market.ts). */
export const PILL_ORDER = { gems: 10, marks: 20 } as const;
/** The pills show once the first level is cleared, when the hub unlocks the Shop and the Armoury. */
export const ECONOMY_FROM_CLEARED = 1;
/** Settings row order, among the platform rows (reminders 40, diagnostics 80, legal 90). */
const RESTORE_ORDER = 70;
/** The debug "Deals: grant offer…" row, after the sandbox store's (MORE_ORDER.debug − 1). */
const DEALS_DEBUG_ORDER = MORE_ORDER.debug + 3;

export function createEconomyFeature(openStore: () => Store | null = safeGameStore): Feature {
  return {
    id: "economy",
    register(ctx, registry) {
      const store = openStore();
      // Nothing is sold in a tab that lost the save to another one: it would never be stored (RC1 P2-4).
      const canPersist = () => saveStore()?.writable ?? true;
      ctx.catalog.canPersist = canPersist;
      const purchases = store ? new PurchaseService({
        store, packs: ctx.balance.commerce.shop.gemPacks, wallet: () => ctx.catalog.economy.wallet, canPersist,
        deals: () => ctx.catalog.deals, balance: ctx.balance,
        // Paid gems: the transaction is finished only once the save holding them is on disk (RC1 P2-3).
        persist: async () => { ctx.persist(); await saveStore()?.flush(); },
        clock: { utcNowSeconds: () => ctx.now() }, log: { log: (level, m) => { if (level !== "info") console.warn(m); } },
      }) : null;
      setEconomyLink({ catalog: ctx.catalog, now: ctx.now, persist: ctx.persist, purchases, storeKind: store?.kind ?? null, hub: null });
      const shopDeps = (hub: HubApi) => ({ hub, catalog: ctx.catalog, now: ctx.now, purchases, storeKind: store?.kind ?? null });

      // The Shop tab opens the Shop sheet (shop redesign 2026-10-19: one sheet, tabs along the top).
      registry.openShop = (hub, page) => { openShopHub(shopDeps(hub), page); };
      // The free daily gold pack (Shop → Supplies) dots the hub's Shop tab until it is claimed (docs/SHOP.md §20).
      registry.tabBadge({ tab: "shop", count: () => (ctx.catalog.highestLevelCleared >= ECONOMY_FROM_CLEARED ? new Economy(ctx.catalog).freePackDot(ctx.now()) : 0) });
      // The hub's only currency (shop redesign 2026-10-19, Kingshot's gold cart): gems, as the Store's button — a tap
      // opens the Store (Deals); its "+" opens the Store on Gems. With no store to sell from (the web build), the Shop.
      registry.topBarPill({
        id: "gems", order: PILL_ORDER.gems,
        html: () => (ctx.catalog.highestLevelCleared >= ECONOMY_FROM_CLEARED ? storeButtonHtml(ctx.catalog.economy.wallet.gems, ctx.catalog.deals.badge(ctx.now(), purchases?.dealsContext() ?? { paidAllowed: false, storefront: null }) + extraTabsDot()) : ""),
        onTap: (hub, _pill, e) => {
          if (ctx.catalog.highestLevelCleared < ECONOMY_FROM_CLEARED) return;
          const d = dealsLink();
          if (d) d.hub = hub;
          const plus = !!(e?.target as HTMLElement | null)?.closest?.(".plus");
          if (!openDeals({ page: plus ? "gems" : null })) openShopHub(shopDeps(hub), "gems");
        },
      });
      registry.hubWidget({
        id: "tribute",
        mount: (hub, layer) => {
          // The widget is the economy's foothold in the hub: pages the hub builds itself reach it through the link.
          const link = economyLink();
          if (link) link.hub = hub;
          const h = mountTributeChip(hub, layer, ctx.catalog, ctx.now);
          return { update: h.update, layout: h.layout, dispose: () => { h.dispose(); const l = economyLink(); if (l && l.hub === hub) l.hub = null; } };
        },
      });
      if (purchases) {
        registry.settingsRow({
          id: "restorePurchases", order: RESTORE_ORDER, scope: "menu",
          // Gems are used up as they are spent, so a store cannot restore them (they live in the save: Save backup keeps
          // them). What this delivers is a payment that went through but never reached the game.
          title: () => "Restore purchases", sub: () => "Gems lost on the road? This brings them home",
          control: () => `<button class="btn dark restore-go"><span>Restore</span></button>`,
          bind: (row, api) => {
            row.querySelector<HTMLButtonElement>(".restore-go")!.onclick = async () => {
              let out: Awaited<ReturnType<typeof purchases.restore>>;
              try { out = await purchases.restore(); } catch { toast(api.parent, "The App Store isn't answering. Try again in a moment"); return; }
              const granted = out.filter((o) => o === "granted").length;
              toast(api.parent, granted > 0 ? `${granted} purchase${granted === 1 ? "" : "s"} delivered: your gems are in` : "All present and accounted for: nothing was missing");
              economyLink()?.hub?.changed();
            };
          },
        });
        bindPurchases(ctx, purchases);
        const sandbox = sandboxStore();
        if (ctx.debug && sandbox) {
          registry.moreRow({
            id: "dealsDebug", order: DEALS_DEBUG_ORDER, icon: "shop",
            label: () => "Debug: Deals — grant offer…", note: () => "Through the sandbox store, at the cheapest picks",
            visible: () => ctx.debug,
            open: (hub) => { showDealsDebug(hub.parent, { balance: ctx.balance, store: sandbox, purchases, changed: () => hub.changed() }); },
          });
        }
      }
      registry.guideTopic(SHOP_TOPIC);
      registry.guideTopic(QUEUE_TOPIC);
      registerReminderSource((now) => tributeReminder(ctx, now));
    },
  };
}

export const economyFeature: Feature = createEconomyFeature();

/** The Store button: the gem heap, the balance in Lilita, the green "+" and a red dot when a deal is new. */
export function storeButtonHtml(gems: number, newDeals: number): string {
  return `<span class="storebtn"><img decoding="sync" src="${currencyIconUrl("gems")}" alt=""><b class="disp">${fmtCompact(gems)}</b><i class="plus" aria-hidden="true">+</i>${newDeals > 0 ? `<i class="dot">${newDeals}</i>` : ""}</span>`;
}

/** The store this build uses; none (the Shop says gem packs come with the iOS app) on the web or when it cannot open. */
function safeGameStore(): Store | null {
  try { return gameStore(); } catch { return null; }
}

/** At boot: grant what a crash left unfinished, then take late transactions (Ask to Buy approved, refunds) as they come. */
function bindPurchases(ctx: FeatureContext, purchases: PurchaseService): void {
  const said = (text: string) => { economyLink()?.hub?.changed(); toast(ctx.ui, text); };
  // The storefront decides which random offers show (docs/SHOP.md flag 1); the Deals screen waits for it on iOS.
  void purchases.refreshStorefront().then(() => economyLink()?.hub?.changed()).catch(() => undefined);
  void purchases.deliverUnfinished().then((outcomes) => {
    const n = outcomes.filter((o) => o === "granted").length;
    if (n > 0) said(`${n === 1 ? "A purchase" : `${n} purchases`} delivered: your gems are in`);
  }).catch(() => undefined);
  purchases.listen((t, outcome) => {
    if (outcome === "granted") econCue("buy");   // a late grant (Ask to Buy approved) sounds like any purchase
    const deal = outcome === "granted" ? purchases.dealGrant(t.transactionId) : null;
    if (deal) said(`${dealName(ctx, deal.offerId)} arrived`);
    else if (outcome === "granted") said(`+${fmtCompact(ctx.catalog.economy.wallet.purchase(t.transactionId)?.gems ?? 0)} gems arrived`);
    else if (outcome === "revoked") said("A refunded purchase was returned to the treasury");
  });
}

/** A Deals offer's name for a toast (Daily Deals tiers by their product name). */
function dealName(ctx: FeatureContext, offerId: string): string {
  return [...dealProducts(ctx.balance).values()].find((p) => p.offerId === offerId)?.name ?? "Your purchase";
}

/** "The Tribute is full" at the moment it fills, while it is unlocked and not full yet (reminders.ts, iOS only). */
function tributeReminder(ctx: FeatureContext, now: number): { id: number; atSeconds: number; title: string; body: string }[] {
  const t = ctx.catalog.economy.tribute, cap = t.capSeconds;
  if (!t.unlocked(ctx.catalog.highestLevelCleared) || t.since < 0 || t.isFull(now) || cap <= 0) return [];
  return [{ id: ReminderIds.tribute, atSeconds: t.since + cap, ...TRIBUTE_REMINDER }];
}
